import { LinkCard } from '@components/LinkCard'
import { RemarkCard } from '@components/RemarkCard'
import { AdditionalInformation } from '@features/ForestOwner/Search/Overview/AdditionalInformation/AdditionalInformation'
import { GeneralInformationCard } from '@features/ForestOwner/Search/Overview/GeneralInformationCard/GeneralInformationCard'
import { SubscriptionCard } from '@features/ForestOwner/Search/Overview/SubscriptionCard/SubscriptionCard'
import { useForestOwnerSandboxProfile } from '@features/Framework/ForestOwnerSandbox/forestOwnerSandboxProfileContext'
import { getForestOwnerQuery } from '@services/queryOptions.ts'
import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useTranslations } from '@utils/i18n-to-migrate/useTranslations'
import { List, MessageCircleMore, Settings2 } from 'lucide-react'
import { useTenant } from '@/contexts/TenantContext'
import { updateForestOwnerRemark } from '@/services/ForestOwners/ForestOwnerService'

export const Route = createFileRoute('/framework/forestowner/$forestownerId/')({
  component: RouteComponent,
  staticData: {
    breadcrumb: 'Oversikt',
  },
})

/**
 * Sandbox Overview tab: composes the *existing* production ForestOwner overview cards
 * (`GeneralInformationCard`, `RemarkCard`, `AdditionalInformation`, `SubscriptionCard`,
 * `LinkCard`) unmodified in structure/behaviour, sourcing their data from the already-loaded
 * `EntityProfileLayout` context instead of each card re-fetching independently. Proves that
 * an entity's Overview tab can be rebuilt from existing production building blocks inside the
 * new framework shell without any endpoint or component-logic changes.
 */
function RouteComponent() {
  const { forestownerId } = Route.useParams()
  const { t } = useTranslations()
  const { isSBSkog, currentTenant } = useTenant()
  const queryClient = useQueryClient()
  const { data } = useForestOwnerSandboxProfile()

  const forestOwnerQuery = getForestOwnerQuery(forestownerId, currentTenant.id)

  if (!data) return null

  return (
    <div className="flex flex-col gap-8">
      <div className="grid grid-cols-3 gap-8">
        <div className="grid col-span-2">
          <GeneralInformationCard forestOwner={data} />
        </div>
        <div className="flex flex-col gap-4 flex-33">
          <RemarkCard
            remark={data.remark}
            onUpdateRemark={async (newRemark) => {
              await updateForestOwnerRemark(data.id, newRemark, data.etag)
            }}
            onSuccess={() => {
              void queryClient.invalidateQueries({
                queryKey: forestOwnerQuery.queryKey,
              })
            }}
          />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-8">
        <LinkCard
          to="/forestowner/$forestownerId/sendSMS"
          params={{ forestownerId }}
          prefixIcon={MessageCircleMore}
          title={t('forestowner-search-overview.smsButton')}
          description={t('forestowner-search-overview.smsLinkDescription')}
        />
        <LinkCard
          to="/forestowner/$forestownerId/contactpreferences"
          params={{ forestownerId }}
          prefixIcon={Settings2}
          title={t('forestowner-search-overview.editContactPrefsButton')}
          description={t('forestowner-search-overview.editContactPrefsLinkDescription')}
        />
        <LinkCard
          to="/"
          prefixIcon={List}
          title={t('forestowner-search-overview.oppdragButton')}
          description={t('forestowner-search-overview.oppdragLinkDescription')}
          disabled
        />
      </div>
      <AdditionalInformation
        additionalInfoLines={data.additionalInfoLines ?? undefined}
        forestOwnerEtag={data.etag}
      />
      {!isSBSkog && (
        <SubscriptionCard
          subscription={data.magazineSubscription ?? undefined}
          forestOwnerEtag={data.etag}
        />
      )}
    </div>
  )
}
